import React from 'react';
import PropTypes from 'prop-types';
import {
  Dribbble,
  Circle,
  CircleDot,
  Triangle,
  PersonStanding,
  Bike,
  Waves,
  Flower2,
  Target,
  Pentagon,
  Check,
} from 'lucide-react';
import { Text } from '@/components/ui/index.js';
import styles from './SportCard.module.css';

/**
 * Icon mapping for sports
 */
const ICON_MAP = {
  dribbble: Dribbble,
  circle: Circle,
  'circle-dot': CircleDot,
  triangle: Triangle,
  'person-standing': PersonStanding,
  bike: Bike,
  waves: Waves,
  'flower-2': Flower2,
  target: Target,
  pentagon: Pentagon,
};

/**
 * SportCard - Individual sport selection card
 * 
 * Features:
 * - Clickable selection
 * - Animated selection state
 * - Icon display
 * - Staggered entrance animation
 */
export function SportCard({
  id,
  label,
  icon,
  isSelected,
  onToggle,
  animationDelay = 0,
}) {
  const IconComponent = ICON_MAP[icon] || Circle;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={handleKeyDown}
      className={`${styles.card} ${isSelected ? styles.cardSelected : ''}`}
      style={{ animationDelay: `${animationDelay}ms` }}
      aria-pressed={isSelected}
      aria-label={`Select ${label}`}
    >
      <div className={styles.cardGlow} />
      
      <div className={styles.cardContent}>
        <div className={styles.iconWrapper}>
          <IconComponent size={28} className={styles.icon} />
        </div>
        
        <Text 
          size="sm" 
          weight="medium"
          color={isSelected ? 'cyan' : 'primary'}
          align="center"
          className={styles.label}
        >
          {label}
        </Text>
      </div>

      {/* Selection indicator */}
      <div className={styles.checkIndicator}>
        <Check size={14} className={styles.checkIcon} />
      </div>
    </div>
  );
}

SportCard.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  animationDelay: PropTypes.number,
};

export default SportCard;
